import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar, Footer } from "../components/Layout";
import { ArrowLeft, ArrowRight, Check, Lock, Sparkles } from "lucide-react";
import { THEME_BUCKETS, findBucketForLesson } from "../lib/lessonThemes";
import { useSEO } from "../hooks/useSEO";

const LEVEL_COLOR = {
  N5: { bg: "bg-[#FFD166]/15", border: "border-[#FFD166]", pill: "bg-[#FFD166] text-[#1A2A40]" },
  N4: { bg: "bg-[#E94A3F]/10", border: "border-[#E94A3F]/40", pill: "bg-[#E94A3F] text-white" },
  N3: { bg: "bg-[#1A2A40]/5", border: "border-[#1A2A40]/20", pill: "bg-[#1A2A40] text-white" },
};

const LessonThemePage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);

  const bucket = THEME_BUCKETS.find((b) => b.slug === slug);

  // SEO meta tags — must run before any conditional return so hook order stays stable
  const canonical = bucket
    ? `https://japonais-pas-a-pas.com/lessons/theme/${bucket.slug}`
    : "https://japonais-pas-a-pas.com/lessons";
  useSEO({
    title: bucket ? `${bucket.seoTitle} | Japonais Pas à Pas` : "Leçons | Japonais Pas à Pas",
    description: bucket?.seoDescription || "",
    keywords: bucket
      ? `${bucket.name}, japonais ${bucket.level}, JLPT ${bucket.level}, apprendre japonais, ${bucket.sub}`
      : undefined,
    canonical,
  });

  useEffect(() => {
    if (bucket) {
      api.get("/lessons").then((r) => setLessons(r.data.data)).catch(() => {});
    }
  }, [bucket]);

  // 404 for unknown slugs — fall back to the full lessons list
  if (!bucket) return <Navigate to="/lessons" replace />;

  const filtered = lessons.filter((l) => {
    const b = findBucketForLesson(l);
    return b && b.slug === bucket.slug;
  });

  const done = new Set(user?.completed_lessons || []);
  const isPremium = !!user?.is_premium;
  const c = LEVEL_COLOR[bucket.level];

  // ItemList JSON-LD for richer SERP
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: bucket.name,
    description: bucket.seoDescription,
    numberOfItems: bucket.count,
    itemListElement: filtered.slice(0, 20).map((l, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: l.title,
      url: `https://japonais-pas-a-pas.com/lessons/${l.id}`,
    })),
  };

  return (
    <div className="washi-bg min-h-screen" data-testid={`lesson-theme-${bucket.slug}`}>
      <Navbar />

      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-6 sm:px-12 pt-6">
        <nav className="text-xs text-[#4A5568]" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#E94A3F]">Accueil</Link>
          <span className="mx-2 text-[#A0AEC0]">/</span>
          <Link to="/lessons" className="hover:text-[#E94A3F]">Leçons</Link>
          <span className="mx-2 text-[#A0AEC0]">/</span>
          <span className="font-bold text-[#1A2A40]">{bucket.name}</span>
        </nav>
      </div>

      {/* Hero */}
      <header className="max-w-6xl mx-auto px-6 sm:px-12 pt-8 pb-12">
        <Link to="/lessons" className="inline-flex items-center gap-1 text-sm text-[#4A5568] hover:text-[#E94A3F] mb-6">
          <ArrowLeft className="w-4 h-4" /> Toutes les leçons
        </Link>

        <div className="flex items-start gap-5 mb-6">
          <div className="text-6xl">{bucket.emoji}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <span className={`${c.pill} text-[10px] font-black tracking-widest px-2 py-1 rounded-full`}>
                {bucket.level}
              </span>
              <span className="text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">
                {bucket.count} leçon{bucket.count > 1 ? "s" : ""}
              </span>
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[#1A2A40] leading-[0.95] tracking-tighter mb-3">
              {bucket.name}.
            </h1>
            <p className="text-base sm:text-lg text-[#4A5568] max-w-3xl">{bucket.sub}</p>
          </div>
        </div>

        {/* Long intro (~80 mots, riche en mots-clés) */}
        <div className={`${c.bg} ${c.border} border-2 rounded-2xl p-6 max-w-4xl`}>
          <p className="text-[#1A2A40] leading-relaxed">{bucket.longIntro}</p>
        </div>
      </header>

      {/* Lessons grid */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 pb-16">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display font-black text-2xl text-[#1A2A40] tracking-tight">
            Toutes les leçons du parcours
          </h2>
          <Link
            to={`/lessons?theme=${bucket.slug}`}
            className="text-xs font-bold text-[#E94A3F] hover:underline inline-flex items-center gap-1"
          >
            Vue compacte avec filtres <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-[#1A2A40]/15 rounded-2xl p-10 text-center">
            <div className="text-4xl mb-3">🍵</div>
            <p className="text-[#4A5568]">Chargement des leçons…</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((l) => {
              const locked = l.premium && !isPremium;
              const isDone = done.has(l.id);
              return (
                <Link
                  key={l.id}
                  to={`/lessons/${l.id}`}
                  data-testid={`theme-lesson-${l.id}`}
                  className={`relative rounded-3xl p-7 border-2 hover-lift block ${
                    isDone ? "bg-[#8CA07A]/10 border-[#8CA07A]" :
                    locked ? "bg-[#F3F6F1] border-[#1A2A40]/10" :
                    "bg-[#FDFBF7] border-[#1A2A40]/10"
                  }`}
                >
                  {locked && (
                    <div className="absolute top-4 right-4 bg-[#1A2A40] text-[#FFD166] rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Premium
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-4">
                    <div className={`text-5xl ${locked ? "opacity-40 grayscale" : ""}`}>{l.emoji}</div>
                    {isDone && <div className="bg-[#8CA07A] text-white rounded-full p-1.5"><Check className="w-4 h-4" /></div>}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#E94A3F] mb-1">{l.level} · {l.duration}</div>
                  <div className={`font-display font-bold text-xl mb-1 ${locked ? "text-[#4A5568]" : "text-[#1A2A40]"}`}>{l.title}</div>
                  <div className="text-sm text-[#4A5568]">{l.subtitle}</div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* CTA + cross-link to other themes (boost internal linking) */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 pb-16">
        <div className="bg-white border-2 border-[#1A2A40] rounded-2xl p-8 shadow-brut-sm">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-[#E94A3F] mb-2">Essai gratuit 3 jours</div>
              <h3 className="font-display font-black text-2xl text-[#1A2A40] mb-2">
                Prêt à commencer ce parcours ?
              </h3>
              <p className="text-sm text-[#4A5568] max-w-md">
                Accède aux {bucket.count} leçons du thème "{bucket.name}" + 200 leçons N5 → N3, tuteur IA, audio natif. Sans carte bancaire.
              </p>
            </div>
            <Link
              to="/register"
              data-testid="theme-cta"
              className="bg-[#E94A3F] hover:bg-[#d83a2f] text-white font-bold py-3.5 px-6 rounded-xl shadow-brut-sm inline-flex items-center gap-2 whitespace-nowrap"
            >
              <Sparkles className="w-4 h-4" /> Commencer gratuitement
            </Link>
          </div>
        </div>

        {/* Cross-link to other themes — boosts SEO + UX */}
        <div className="mt-12">
          <h3 className="font-display font-black text-xl text-[#1A2A40] mb-4 tracking-tight">
            Explorer d'autres parcours
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {THEME_BUCKETS.filter((b) => b.slug !== bucket.slug).slice(0, 8).map((b) => {
              const oc = LEVEL_COLOR[b.level];
              return (
                <Link
                  key={b.slug}
                  to={`/lessons/theme/${b.slug}`}
                  data-testid={`theme-cross-${b.slug}`}
                  className={`group ${oc.bg} ${oc.border} border-2 rounded-xl p-4 hover:scale-[1.02] transition`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xl">{b.emoji}</span>
                    <span className={`${oc.pill} text-[9px] font-black tracking-widest px-1.5 py-0.5 rounded-full`}>
                      {b.level}
                    </span>
                  </div>
                  <div className="font-display font-bold text-sm text-[#1A2A40] leading-tight">
                    {b.name}
                  </div>
                  <div className="text-[10px] text-[#4A5568] mt-1">{b.count} leçons</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* JSON-LD ItemList for SERP */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Footer />
    </div>
  );
};

export default LessonThemePage;
